<script setup lang="ts">
import type {
  FramePerformanceSnapshot,
  PassTimingEntry,
  PerformanceMonitorHudPosition,
} from '~/lib/nijilucid'

/**
 * Монитор производительности апскейла — порт HUD из NijiLucid
 * (core/overlay-manager.ts), переписанный на Vue.
 *
 * Два вида: свёрнутый — одна строка «FPS / CPU / GPU / пропуски», и полный —
 * с GPU, способом передачи кадра, режимом, разрешениями и раскладкой времени
 * по эффектам. Раскладка по эффектам есть только в режиме «диагностика GPU»
 * и только если браузер отдаёт timestamp-query; иначе — сводка по CPU и
 * подсказка, почему подробностей нет.
 *
 * Углы, ширина и свёрнутость — настройки пользователя, живут снаружи (в
 * настройках апскейла), здесь только события.
 */
const props = defineProps<{
  snapshot: FramePerformanceSnapshot
  collapsed: boolean
  position: PerformanceMonitorHudPosition
  width: number | null
}>()

const emit = defineEmits<{
  'update:collapsed': [value: boolean]
  'update:position': [value: PerformanceMonitorHudPosition]
  'update:width': [value: number]
  close: []
}>()

// Підписи українською — у референсному плеєрі вони жили в спільному i18n,
// тут словник один і маленький. t(key) у шаблоні, t.value(key) у коді —
// щоб код лишався таким самим, як в апстримі
const HUD_TEXT: Record<string, string> = {
  'upscaler.monitor': 'Монітор продуктивності',
  'upscaler.hudCollapse': 'Згорнути',
  'upscaler.hudCopy': 'Копіювати',
  'upscaler.hudClose': 'Закрити',
  'upscaler.hudMove': 'Перемістити в інший кут',
  'upscaler.hudUpload': 'Передача',
  'upscaler.hudMode': 'Режим',
  'upscaler.hudSource': 'Джерело',
  'upscaler.hudDrop': 'Пропуски',
  'upscaler.hudRenderTime': 'Час рендерингу',
  'upscaler.hudTotal': 'Разом',
  'upscaler.hudFrameCpu': 'CPU кадру',
  'upscaler.hudUploadCpu': 'CPU передачі',
  'upscaler.hudEncodeCpu': 'CPU кодування',
  'upscaler.hudSubmitCpu': 'CPU відправлення',
  'upscaler.hudSnapshotTitle': 'Монітор продуктивності апскейлу',
  'upscaler.hudTimingLiteHint':
    'Для вимірювання часу ефектів потрібна діагностика GPU. Полегшений режим показує лише загальну статистику кадрів.',
  'upscaler.hudTimingUnavailableHint':
    'Запити часових міток GPU недоступні, тому час ефектів не можна виміряти точно.',
  'upscaler.hudTimingWaitingHint':
    'Очікування вибірок часових міток GPU. Час ефектів зʼявиться, коли будуть доступні дані діагностики GPU.'
}
const t: any = (key: string) => HUD_TEXT[key] ?? key
t.value = t

// Палитра сайта: primary, secondary, accent, success, warning, info…
const HUD_COLORS = ['#38bdf8', '#818CF8', '#F471B5', '#2DD4BF', '#F4BF50', '#0CA5E9', '#FB7085', '#a3e635']
const HUD_MIN_WIDTH = 260
const HUD_MAX_WIDTH = 640
const HUD_DEFAULT_WIDTH = 360

/**
 * HUD обновляется не чаще двух раз в секунду: снимки прилетают на каждый
 * кадр, а мельтешащие цифры читать нельзя.
 */
const HUD_UPDATE_INTERVAL_MS = 500
const shown = shallowRef<FramePerformanceSnapshot>(props.snapshot)
let lastShownAt = 0
let pendingTimer: ReturnType<typeof setTimeout> | null = null

watch(
  () => props.snapshot,
  (next) => {
    const now = performance.now()
    if (now - lastShownAt >= HUD_UPDATE_INTERVAL_MS) {
      lastShownAt = now
      shown.value = next
      return
    }
    if (pendingTimer) return
    pendingTimer = setTimeout(() => {
      pendingTimer = null
      lastShownAt = performance.now()
      shown.value = props.snapshot
    }, HUD_UPDATE_INTERVAL_MS - (now - lastShownAt))
  }
)

onBeforeUnmount(() => {
  if (pendingTimer) clearTimeout(pendingTimer)
})

const rootRef = ref<HTMLElement | null>(null)
const renderTimingCollapsed = ref(false)

const statusClass = computed(() => {
  const s = shown.value
  if (s.frameMs > s.budgetMs) return 'is-over'
  if (s.frameMs > s.budgetMs * 0.8) return 'is-warn'
  return ''
})

// ─── Форматирование ─────────────────────────────────────────────────────────

function formatFps(value: number): string {
  return Number.isFinite(value) ? value.toFixed(1) : '0.0'
}

function formatMs(value: number | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${value.toFixed(2)} ms` : 'n/a'
}

function entryDisplayMs(entry: PassTimingEntry): number {
  return entry.cpuMs + (entry.gpuMs ?? 0)
}

function formatEntryMs(entry: PassTimingEntry): string {
  const cpu = `CPU ${formatMs(entry.cpuMs)}`
  return typeof entry.gpuMs === 'number' ? `${cpu} / GPU ${formatMs(entry.gpuMs)}` : cpu
}

function sumGpuMs(entries: PassTimingEntry[]): number | undefined {
  const values = entries.map((e) => e.gpuMs).filter((v): v is number => typeof v === 'number')
  return values.length ? values.reduce((a, b) => a + b, 0) : undefined
}

const gpuTotalText = computed(() => {
  const s = shown.value
  return s.timestampAvailable ? `GPU ${formatMs(sumGpuMs(s.groupEntries))}` : 'GPU n/a'
})

const dropText = computed(() => `${(shown.value.droppedFrameRate * 100).toFixed(1)}%`)

/** Раскладка по эффектам показывается, только когда есть что показать. */
const passTimings = computed(() => {
  const s = shown.value
  const groups = s.groupEntries
  const hasGpuPassTimings =
    s.mode === 'gpu' && s.timestampAvailable && groups.some((e) => typeof e.gpuMs === 'number')
  const canShow = groups.length > 0 && (s.mode !== 'gpu' || hasGpuPassTimings)
  if (!canShow) return null

  const total = groups.reduce((sum, e) => sum + entryDisplayMs(e), 0)
  const hasGpu = groups.some((e) => typeof e.gpuMs === 'number')
  return {
    rows: groups.map((entry, index) => ({
      label: entry.label,
      value: formatEntryMs(entry),
      color: HUD_COLORS[index % HUD_COLORS.length],
      widthPct: total > 0 ? Math.max(2, (entryDisplayMs(entry) / total) * 100) : 0,
    })),
    totalLabel: `${hasGpu ? 'CPU+GPU' : 'CPU'} ${formatMs(total)}`,
  }
})

const timingHint = computed(() => {
  const s = shown.value
  if (s.mode === 'lite') return t.value('upscaler.hudTimingLiteHint')
  if (!s.timestampAvailable) return t.value('upscaler.hudTimingUnavailableHint')
  return t.value('upscaler.hudTimingWaitingHint')
})

// ─── Действия ───────────────────────────────────────────────────────────────

function nextPosition(position: PerformanceMonitorHudPosition): PerformanceMonitorHudPosition {
  switch (position) {
    case 'top-left':
      return 'top-right'
    case 'top-right':
      return 'bottom-right'
    case 'bottom-right':
      return 'bottom-left'
    default:
      return 'top-left'
  }
}

function snapshotText(): string {
  const s = shown.value
  const lines = [
    t.value('upscaler.hudSnapshotTitle'),
    `GPU: ${s.gpuName}`,
    `${t.value('upscaler.hudUpload')}: ${s.uploadMethod}`,
    `${t.value('upscaler.hudMode')}: ${s.modeName} / ${s.tier}`,
    `${t.value('upscaler.hudSource')}: ${s.sourceDimensions.width}x${s.sourceDimensions.height} -> ${s.targetDimensions.width}x${s.targetDimensions.height}`,
    `FPS: ${formatFps(s.fps)}`,
    `${t.value('upscaler.hudFrameCpu')}: ${formatMs(s.frameMs)}`,
    `${t.value('upscaler.hudUploadCpu')}: ${formatMs(s.uploadMs)}`,
    `${t.value('upscaler.hudEncodeCpu')}: ${formatMs(s.encodeMs)}`,
    `${t.value('upscaler.hudSubmitCpu')}: ${formatMs(s.submitMs)}`,
    `${t.value('upscaler.hudDrop')}: ${dropText.value}`,
  ]
  const timings = passTimings.value
  if (!timings) {
    lines.push(timingHint.value)
  } else {
    lines.push(...timings.rows.map((r) => `${r.label}: ${r.value}`))
  }
  return lines.join('\n')
}

function copySnapshot() {
  navigator.clipboard?.writeText(snapshotText()).catch(() => {
    /* без clipboard API — ну и ладно */
  })
}

// ─── Ширина: перетаскивание за уголок ───────────────────────────────────────

function hostWidth(): number {
  return rootRef.value?.parentElement?.clientWidth || window.innerWidth || HUD_MAX_WIDTH
}

function clampWidth(width: number): number {
  const host = hostWidth()
  const available = host > 16 ? host - 16 : HUD_MAX_WIDTH
  const max = Math.min(HUD_MAX_WIDTH, Math.max(160, available))
  const min = Math.min(HUD_MIN_WIDTH, max)
  return Math.round(Math.min(max, Math.max(min, width)))
}

const renderWidth = computed(() => {
  if (props.collapsed) return undefined
  const host = hostWidth()
  const fallback = host > 0 ? Math.min(HUD_DEFAULT_WIDTH, Math.max(HUD_MIN_WIDTH, host * 0.62)) : HUD_DEFAULT_WIDTH
  return `${clampWidth(props.width ?? fallback)}px`
})

let resize: { pointerId: number; startX: number; startWidth: number } | null = null

function onGripDown(event: PointerEvent) {
  const el = rootRef.value
  if (!el) return
  event.preventDefault()
  resize = { pointerId: event.pointerId, startX: event.clientX, startWidth: el.getBoundingClientRect().width }
  el.setPointerCapture(event.pointerId)
}

function onPointerMove(event: PointerEvent) {
  const el = rootRef.value
  if (!resize || !el) return
  const direction = props.position.includes('right') ? -1 : 1
  el.style.width = `${clampWidth(resize.startWidth + (event.clientX - resize.startX) * direction)}px`
}

function onPointerUp(event: PointerEvent) {
  const el = rootRef.value
  if (!resize || !el) return
  if (el.hasPointerCapture(resize.pointerId)) el.releasePointerCapture(resize.pointerId)
  resize = null
  emit('update:width', clampWidth(el.getBoundingClientRect().width))
}
</script>

<template>
  <section
    ref="rootRef"
    class="shader-hud"
    :class="[position, { 'is-collapsed': collapsed }, statusClass]"
    :style="{ width: renderWidth }"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
  >
    <!-- Свёрнутый вид: одна строка, клик разворачивает -->
    <button v-if="collapsed" type="button" class="hud-mini" @click="emit('update:collapsed', false)">
      <span>{{ formatFps(shown.fps) }} FPS</span>
      <span>CPU {{ formatMs(shown.frameMs) }}</span>
      <span>{{ gpuTotalText }}</span>
      <span>{{ t('upscaler.hudDrop') }} {{ dropText }}</span>
    </button>

    <template v-else>
      <div class="hud-titlebar">
        <button
          type="button"
          class="hud-icon hud-collapse-button"
          :title="t('upscaler.hudCollapse')"
          @click="emit('update:collapsed', true)"
        >
          <span class="hud-chevron" />
        </button>
        <span class="hud-title">{{ t('upscaler.monitor') }}</span>
        <button
          type="button"
          class="hud-icon"
          :title="t('upscaler.hudMove')"
          @click="emit('update:position', nextPosition(position))"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4 14v6h6M20 10V4h-6M4 20l6-6M20 4l-6 6" />
          </svg>
        </button>
        <button type="button" class="hud-icon" :title="t('upscaler.hudCopy')" @click="copySnapshot">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="9" y="9" width="12" height="12" rx="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        </button>
        <button type="button" class="hud-icon" :title="t('upscaler.hudClose')" @click="emit('close')">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      <div class="hud-body">
        <div class="hud-info">GPU: {{ shown.gpuName }}</div>
        <div class="hud-info">{{ t('upscaler.hudUpload') }}: {{ shown.uploadMethod }}</div>
        <div class="hud-info">{{ t('upscaler.hudMode') }}: {{ shown.modeName }} / {{ shown.tier }}</div>
        <div class="hud-info">
          {{ t('upscaler.hudSource') }}: {{ shown.sourceDimensions.width }}x{{ shown.sourceDimensions.height }} →
          {{ shown.targetDimensions.width }}x{{ shown.targetDimensions.height }}
        </div>
        <div class="hud-metrics">
          <span>FPS: {{ formatFps(shown.fps) }}</span>
          <span>{{ t('upscaler.hudDrop') }}: {{ dropText }}</span>
          <span>{{ gpuTotalText }}</span>
        </div>

        <div class="hud-section">
          <button
            type="button"
            class="hud-section-title"
            :class="{ 'is-collapsed': renderTimingCollapsed }"
            @click="renderTimingCollapsed = !renderTimingCollapsed"
          >
            <span class="hud-chevron" />
            <span>{{ t('upscaler.hudRenderTime') }}</span>
          </button>

          <template v-if="!renderTimingCollapsed">
            <template v-if="passTimings">
              <div class="hud-stack" :title="`${passTimings.totalLabel} / ${formatMs(shown.budgetMs)}`">
                <span
                  v-for="row in passTimings.rows"
                  :key="row.label"
                  :style="{ width: `${row.widthPct.toFixed(2)}%`, background: row.color }"
                />
              </div>
              <div v-for="row in passTimings.rows" :key="row.label" class="hud-row">
                <span class="hud-swatch" :style="{ background: row.color }" />
                <span class="hud-name">{{ row.label }}</span>
                <span class="hud-ms">{{ row.value }}</span>
              </div>
              <div class="hud-rule" />
              <div class="hud-row hud-total">
                <span />
                <span class="hud-name">{{ t('upscaler.hudTotal') }}</span>
                <span class="hud-ms">{{ passTimings.totalLabel }}</span>
              </div>
            </template>

            <template v-else>
              <div class="hud-cpu-summary">
                <div class="hud-row hud-summary-row">
                  <span /><span class="hud-name">{{ t('upscaler.hudFrameCpu') }}</span>
                  <span class="hud-ms">{{ formatMs(shown.frameMs) }}</span>
                </div>
                <div class="hud-row hud-summary-row">
                  <span /><span class="hud-name">{{ t('upscaler.hudUploadCpu') }}</span>
                  <span class="hud-ms">{{ formatMs(shown.uploadMs) }}</span>
                </div>
                <div class="hud-row hud-summary-row">
                  <span /><span class="hud-name">{{ t('upscaler.hudEncodeCpu') }}</span>
                  <span class="hud-ms">{{ formatMs(shown.encodeMs) }}</span>
                </div>
                <div class="hud-row hud-summary-row">
                  <span /><span class="hud-name">{{ t('upscaler.hudSubmitCpu') }}</span>
                  <span class="hud-ms">{{ formatMs(shown.submitMs) }}</span>
                </div>
              </div>
              <div class="hud-hint">{{ timingHint }}</div>
            </template>
          </template>
        </div>

        <span class="hud-resize-grip" @pointerdown="onGripDown" />
      </div>
    </template>
  </section>
</template>

<style scoped>
/* В цветах сайта: тёмно-синий neutral, как у меню плеера, и голубой primary */
.shader-hud {
  --hud-primary: hsl(var(--p));
  --hud-primary-soft: hsl(var(--p) / 0.16);
  --hud-surface: hsl(var(--n) / 0.92);
  --hud-surface-high: hsl(var(--nf) / 0.95);
  --hud-outline: hsl(var(--nc) / 0.1);
  --hud-text: hsl(var(--nc));
  --hud-text-muted: hsl(var(--nc) / 0.6);
  position: absolute;
  z-index: 25;
  width: min(360px, calc(100% - 16px));
  min-width: min(260px, calc(100% - 16px));
  max-height: calc(100% - 16px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  pointer-events: auto;
  color: var(--hud-text);
  background: var(--hud-surface);
  border: 1px solid var(--hud-outline);
  border-radius: 0.5rem;
  box-shadow: 0 20px 25px -5px rgb(0 0 0 / 0.3), 0 8px 10px -6px rgb(0 0 0 / 0.3);
  font: 13px/1.3 inherit;
  font-family: inherit;
  backdrop-filter: blur(10px);
  user-select: none;
}

.shader-hud.is-collapsed {
  width: auto;
  min-width: 0;
}

.shader-hud.top-left {
  top: 8px;
  left: 8px;
}

.shader-hud.top-right {
  top: 8px;
  right: 8px;
}

.shader-hud.bottom-left {
  bottom: 8px;
  left: 8px;
}

.shader-hud.bottom-right {
  right: 8px;
  bottom: 8px;
}

.hud-titlebar {
  display: grid;
  grid-template-columns: auto 1fr auto auto auto;
  align-items: center;
  gap: 4px;
  min-height: 34px;
  padding: 0 6px 0 8px;
  background: var(--hud-surface-high);
  border-bottom: 1px solid var(--hud-outline);
}

.hud-title {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 13px;
  font-weight: 600;
}

.hud-icon,
.hud-mini {
  border: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
}

.hud-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border-radius: 0.375rem;
  font-size: 15px;
  line-height: 22px;
  color: var(--hud-text-muted);
}

.hud-icon:hover,
.hud-mini:hover {
  background: var(--hud-primary-soft);
  color: var(--hud-text);
}

.hud-chevron {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg);
  transition: transform 0.15s;
}

.hud-collapse-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--hud-primary);
}

.hud-body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 10px 12px;
}

.hud-info,
.hud-metrics {
  margin-bottom: 3px;
  font-size: 12px;
}

.hud-info {
  color: var(--hud-text-muted);
}

.hud-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
  margin-top: 6px;
  padding: 6px 8px;
  border-radius: 0.375rem;
  background: hsl(var(--b1) / 0.5);
  color: var(--hud-text);
  font-variant-numeric: tabular-nums;
}

.hud-section {
  margin-top: 8px;
}

.hud-section-title {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 7px;
  width: 100%;
  margin: 0 0 6px;
  padding: 4px 8px;
  border: 0;
  border-radius: 0.375rem;
  color: var(--hud-text-muted);
  background: transparent;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-family: inherit;
  text-align: left;
}

.hud-section-title .hud-chevron {
  color: var(--hud-primary);
}

.hud-section-title:hover {
  background: var(--hud-primary-soft);
  color: var(--hud-text);
}

.hud-section-title.is-collapsed .hud-chevron {
  transform: rotate(-45deg);
}

.hud-stack {
  display: flex;
  height: 8px;
  margin: 0 0 8px;
  overflow: hidden;
  border-radius: 9999px;
  background: hsl(var(--b1) / 0.6);
}

.hud-stack span {
  display: block;
  min-width: 1px;
}

.hud-row {
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr) minmax(78px, auto);
  align-items: center;
  gap: 6px;
  min-height: 20px;
  font-size: 12px;
}

.hud-swatch {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
}

.hud-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.hud-ms {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.hud-rule {
  height: 1px;
  margin: 6px 0;
  background: var(--hud-outline);
}

.hud-total {
  font-size: 12px;
  font-weight: 600;
}

.hud-cpu-summary {
  padding: 2px 0 4px;
}

.hud-summary-row {
  color: var(--hud-text-muted);
}

.hud-hint {
  margin-top: 6px;
  padding: 7px 8px;
  border-radius: 0.375rem;
  color: var(--hud-text-muted);
  background: hsl(var(--b1) / 0.5);
  font-size: 11px;
  line-height: 1.35;
}

.shader-hud.is-warn .hud-ms,
.shader-hud.is-warn .hud-mini span:nth-child(2) {
  color: hsl(var(--wa));
}

.shader-hud.is-over .hud-ms,
.shader-hud.is-over .hud-mini span:nth-child(2) {
  color: hsl(var(--er));
}

.hud-mini {
  display: grid;
  grid-template-columns: repeat(4, max-content);
  gap: 8px;
  width: 100%;
  min-height: 28px;
  padding: 4px 10px;
  border-left: 3px solid var(--hud-primary);
  font-size: 12px;
  line-height: 1.3;
  font-variant-numeric: tabular-nums;
  text-align: left;
}

.hud-resize-grip {
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 13px;
  height: 13px;
  cursor: nwse-resize;
  opacity: 0.5;
  touch-action: none;
}

.hud-resize-grip::before {
  content: '';
  position: absolute;
  right: 2px;
  bottom: 2px;
  width: 8px;
  height: 8px;
  background:
    linear-gradient(135deg, transparent 45%, var(--hud-text-muted) 47%, var(--hud-text-muted) 53%, transparent 55%),
    linear-gradient(135deg, transparent 64%, var(--hud-text-muted) 66%, var(--hud-text-muted) 72%, transparent 74%);
}
</style>
